import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import ContactCard from '../../../contacts/ContactCard';
import ContactsModal, { Contact } from '../../../contacts/ContactsModal';
import ConfirmDeleteModal from '../../../../organisms/ConfirmDeleteModal';
import api, { getUserUid } from '../../../../../utils/api';
import { API_ENDPOINTS } from '../../../../../config';

interface ContactsTabProps {
  jobId?: string;
  boardId?: string;
  shouldCreateContact?: boolean;
  onContactModalOpened?: () => void;
  highlightEntityUid?: string | null;
}

interface ContactFromAPI {
  uid: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  companies_name?: string[];
  email?: string[];
  phone?: string[];
  linkedin_url?: string;
  twitter_url?: string;
  meta_url?: string;
  github_url?: string;
  board_linked?: string;
  job_linked?: string;
}

interface ContactModalData {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  phone?: string;
  twitter?: string;
  facebook?: string;
  linkedin?: string;
  github?: string;
  boardLinked?: string;
  jobLinked?: string;
}

export default function ContactsTab({ jobId, boardId, shouldCreateContact, onContactModalOpened, highlightEntityUid }: ContactsTabProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactModalData | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    contactId: string | null;
    contactName: string;
  }>({
    isOpen: false,
    contactId: null,
    contactName: '',
  });

  const transformContact = (apiContact: ContactFromAPI): Contact => {
    if (!apiContact.uid) {

      throw new Error('Contact must have uid');
    }
    return {
      id: apiContact.uid,
      firstName: apiContact.first_name,
      lastName: apiContact.last_name,
      title: apiContact.job_title || '',
      company: apiContact.companies_name?.[0] || '',
      email: apiContact.email?.[0] || '',
      phone: apiContact.phone?.[0] || '',
      twitter: apiContact.twitter_url || undefined,
      linkedin: apiContact.linkedin_url || undefined,
      facebook: apiContact.meta_url || undefined,
      github: apiContact.github_url || undefined,
      boardLinked: apiContact.board_linked,
      jobLinked: apiContact.job_linked,
    };
  };

  useEffect(() => {
    const fetchContacts = async () => {
      if (!boardId) {
        setContacts([]);
        setLoadingContacts(false);
        return;
      }

      try {
        setLoadingContacts(true);
        const data = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.getByKanban(boardId));
        
        if (Array.isArray(data)) {
          const filteredData = jobId 
            ? data.filter(contact => contact.job_linked && String(contact.job_linked) === String(jobId))
            : data;
          
          const transformedContacts = filteredData.map(transformContact);
          setContacts(transformedContacts);
        } else {
          setContacts([]);
        }
      } catch (error) {

        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchContacts();
  }, [boardId, jobId]);

  const refreshContacts = async () => {
    if (!boardId) return;

    try {
      const data = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.getByKanban(boardId));
      if (Array.isArray(data)) {
        const filteredData = jobId 
          ? data.filter(contact => contact.job_linked && String(contact.job_linked) === String(jobId))
          : data;
        
        const transformedContacts = filteredData.map(transformContact);
        setContacts(transformedContacts);
      }
    } catch (error) {

    }
  };

  const handleNewContact = () => {
    setEditingContact({
      boardLinked: boardId,
      jobLinked: jobId,
    });
    setEditingContactId(null);
    setModalOpen(true);
  };

  useEffect(() => {
    if (shouldCreateContact) {
      setEditingContact({
        boardLinked: boardId,
        jobLinked: jobId,
      });
      setEditingContactId(null);
      setModalOpen(true);
      if (onContactModalOpened) {
        onContactModalOpened();
      }
    }
  }, [shouldCreateContact, boardId, jobId, onContactModalOpened]);

  useEffect(() => {
    if (!highlightEntityUid || contacts.length === 0) return;
    const el = document.querySelector(`[data-uid="${highlightEntityUid}"]`) as HTMLElement | null;
    if (el) {
      el.classList.add('ejm-entity-highlight');
      const t = setTimeout(() => el.classList.remove('ejm-entity-highlight'), 3500);
      return () => clearTimeout(t);
    }
  }, [highlightEntityUid, contacts]);

  const handleEditContact = (id: string) => {
    const contact = contacts.find(c => c.id === id);
    if (contact && contact.id) {
      setEditingContact({
        firstName: contact.firstName,
        lastName: contact.lastName,
        title: contact.title,
        company: contact.company,
        email: contact.email || undefined,
        phone: contact.phone || undefined,
        twitter: contact.twitter,
        linkedin: contact.linkedin,
        facebook: contact.facebook,
        github: contact.github,
        boardLinked: contact.boardLinked,
        jobLinked: contact.jobLinked,
      });
      setEditingContactId(contact.id);
      setModalOpen(true);
    }
  };

  const handleDeleteContact = (id: string) => {
    const contact = contacts.find(c => c.id === id);
    if (contact) {
      setDeleteModal({
        isOpen: true,
        contactId: id,
        contactName: `${contact.firstName} ${contact.lastName}`.trim() || 'this contact',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.contactId) {
      setDeleteModal({ isOpen: false, contactId: null, contactName: '' });
      return;
    }

    const contactIdToDelete = deleteModal.contactId;
    
    setContacts(prev => prev.filter(c => c.id !== contactIdToDelete));
    setDeleteModal({ isOpen: false, contactId: null, contactName: '' });

    try {
      await api.delete(API_ENDPOINTS.contacts.delete(contactIdToDelete));
      await refreshContacts();
    } catch (error: any) {

      await refreshContacts();
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, contactId: null, contactName: '' });
  };

  const handleSubmitContact = async (data: ContactModalData) => {
    try {
      const user_uid = getUserUid();
      if (!user_uid) {

        return;
      }

      if (editingContactId) {
        const contactPayload = {
          first_name: data.firstName || '',
          last_name: data.lastName || '',
          job_title: data.title || null,
          companies_name: data.company ? [data.company] : [],
          email: data.email ? [data.email] : [],
          phone: data.phone ? [data.phone] : [],
          linkedin_url: data.linkedin || null,
          twitter_url: data.twitter || null,
          meta_url: data.facebook || null,
          github_url: data.github || null,
          board_linked: data.boardLinked || null,
          job_linked: data.jobLinked || null,
        };

        await api.put(API_ENDPOINTS.contacts.update(editingContactId), contactPayload);
      } else {
        const contactPayload = {
          first_name: data.firstName || '',
          last_name: data.lastName || '',
          job_title: data.title || null,
          companies_name: data.company ? [data.company] : [],
          email: data.email ? [data.email] : [],
          phone: data.phone ? [data.phone] : [],
          linkedin_url: data.linkedin || null,
          twitter_url: data.twitter || null,
          meta_url: data.facebook || null,
          github_url: data.github || null,
          board_linked: data.boardLinked || null,
          job_linked: data.jobLinked || null,
        };

        await api.post(API_ENDPOINTS.contacts.create, contactPayload);
      }

      await refreshContacts();
      setModalOpen(false);
      setEditingContact(null);
      setEditingContactId(null);
    } catch (error) {

    }
  };

  return (
    <div className="contacts-tab-wrapper">
      <div className="contacts-tab-header">
        <button
          className="btn-new-contact"
          onClick={handleNewContact}
          type="button"
        >
          + Contact
        </button>
      </div>

      {loadingContacts ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
          Loading contacts...
        </div>
      ) : (
        <>
          {contacts.length === 0 && (
            <div className="contacts-tab-empty">
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                <Users size={32} style={{ color: '#9ca3af' }} />
              </div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>No contacts yet</div>
              <div>Click "+ Contact" to add your first contact</div>
            </div>
          )}

          <div className="contacts-tab-grid">
            {contacts.map(contact => {
              if (!contact.id) return null;
              return (
                <div key={contact.id} data-uid={contact.id}>
                  <ContactCard
                    firstName={contact.firstName}
                    lastName={contact.lastName}
                    title={contact.title || ''}
                    company={contact.company || ''}
                    location={contact.location}
                    email={contact.email || ''}
                    phone={contact.phone}
                    linkedIn={contact.linkedin}
                    onEdit={() => handleEditContact(contact.id!)}
                    onDelete={() => handleDeleteContact(contact.id!)}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      <ContactsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingContact(null);
          setEditingContactId(null);
        }}
        initial={editingContact}
        onSubmit={handleSubmitContact}
        boardId={editingContact?.boardLinked || boardId}
      />

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        itemName={deleteModal.contactName}
        itemType="contact"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
